import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, LogOut } from "lucide-react";
import AdminPhotos from "@/pages/admin/AdminPhotos";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_STATUSES = ["pending", "confirmed", "cancelled", "completed"];
const ENQUIRY_STATUSES = ["new", "in-progress", "resolved", "archived"];
const OUTING_STATUSES = ["booked", "visited", "no-show", "cancelled"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [token] = useState(() => localStorage.getItem("tikra_token"));
  const [email] = useState(() => localStorage.getItem("tikra_email"));
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [outings, setOutings] = useState([]);

  useEffect(() => {
    if (!token) nav("/admin/login");
  }, [token, nav]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [s, b, e, o] = await Promise.all([
        axios.get(`${API}/stats`, auth),
        axios.get(`${API}/bookings`, auth),
        axios.get(`${API}/enquiries`, auth),
        axios.get(`${API}/day-outings`, auth),
      ]);
      setStats(s.data); setBookings(b.data); setEnquiries(e.data); setOutings(o.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("tikra_token");
        nav("/admin/login");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const logout = () => {
    localStorage.removeItem("tikra_token");
    localStorage.removeItem("tikra_email");
    nav("/admin/login");
  };

  const updateBooking = async (id, status) => {
    await axios.patch(`${API}/bookings/${id}`, { status }, auth);
    toast.success("Booking updated");
    load();
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await axios.delete(`${API}/bookings/${id}`, auth);
    toast.success("Deleted");
    load();
  };

  const updateEnquiry = async (id, status) => {
    await axios.patch(`${API}/enquiries/${id}`, { status }, auth);
    toast.success("Enquiry updated");
    load();
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await axios.delete(`${API}/enquiries/${id}`, auth);
    toast.success("Deleted");
    load();
  };

  const updateOuting = async (id, status) => {
    await axios.patch(`${API}/day-outings/${id}`, { status }, auth);
    toast.success("Day Outing updated");
    load();
  };

  const deleteOuting = async (id) => {
    if (!window.confirm("Delete this Day Outing?")) return;
    await axios.delete(`${API}/day-outings/${id}`, auth);
    toast.success("Deleted");
    load();
  };

  const Stat = ({ label, value }) => (
    <div className="border border-white/10 p-6">
      <div className="chapter-num">{label}</div>
      <div className="font-display text-5xl mt-2 text-[#F2E7D0]">{value ?? "—"}</div>
    </div>
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#1C1913] text-[#F2E7D0]">
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-6 flex items-center justify-between">
          <div>
            <div className="chapter-num">Console</div>
            <div className="font-display text-3xl">Tikratoli Admin</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono-label text-[#B8A891]">{email}</span>
            <Button
              data-testid="admin-logout"
              onClick={logout}
              variant="ghost"
              className="font-mono-label border border-white/20 rounded-none"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Bookings" value={stats?.total_bookings} />
        <Stat label="Pending" value={stats?.pending_bookings} />
        <Stat label="Confirmed" value={stats?.confirmed_bookings} />
        <Stat label="Enquiries" value={stats?.total_enquiries} />
        <Stat label="New enquiries" value={stats?.new_enquiries} />
      </section>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-6">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-transparent border border-white/10 rounded-none p-1">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none font-mono-label data-[state=active]:bg-[#D97645] data-[state=active]:text-[#F2E7D0]">
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-none font-mono-label data-[state=active]:bg-[#D97645] data-[state=active]:text-[#F2E7D0]">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="outings" data-testid="tab-outings" className="rounded-none font-mono-label data-[state=active]:bg-[#D97645] data-[state=active]:text-[#F2E7D0]">
              Day Outings ({outings.length})
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-none font-mono-label data-[state=active]:bg-[#D97645] data-[state=active]:text-[#F2E7D0]">
              Photos
            </TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts" className="rounded-none font-mono-label data-[state=active]:bg-[#D97645] data-[state=active]:text-[#F2E7D0]">
              Discounts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="border border-white/10 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="font-mono-label text-[#B8A891]">Guest</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Contact</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Dates</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Room</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Guests</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#B8A891] py-8">No bookings yet.</TableCell></TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="border-white/10">
                      <TableCell className="text-[#F2E7D0]">{b.full_name}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{b.email}<br/>{b.phone}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{b.check_in} → {b.check_out}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{b.room_type}</TableCell>
                      <TableCell className="text-[#B8A891]">{b.guests}</TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateBooking(b.id, v)}>
                          <SelectTrigger className="h-9 rounded-none bg-transparent border-white/20 w-36 font-mono-label text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                            {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button data-testid={`delete-booking-${b.id}`} onClick={() => deleteBooking(b.id)} variant="ghost" size="icon"><Trash2 size={16} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            <div className="border border-white/10 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="font-mono-label text-[#B8A891]">Name</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Contact</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Subject</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Message</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-[#B8A891] py-8">No enquiries yet.</TableCell></TableRow>
                  )}
                  {enquiries.map((e) => (
                    <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-white/10">
                      <TableCell className="text-[#F2E7D0]">{e.name}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{e.email}<br/>{e.phone}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{e.subject}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs max-w-xs truncate">{e.message}</TableCell>
                      <TableCell>
                        <Select value={e.status} onValueChange={(v) => updateEnquiry(e.id, v)}>
                          <SelectTrigger className="h-9 rounded-none bg-transparent border-white/20 w-36 font-mono-label text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                            {ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button data-testid={`delete-enquiry-${e.id}`} onClick={() => deleteEnquiry(e.id)} variant="ghost" size="icon"><Trash2 size={16} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="photos" className="mt-6">
            <AdminPhotos token={token} />
          </TabsContent>

          <TabsContent value="outings" className="mt-6">
            <div className="border border-white/10 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="font-mono-label text-[#B8A891]">Guest</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Contact</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Date</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Arrival</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Guests</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Paid</TableHead>
                    <TableHead className="font-mono-label text-[#B8A891]">Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outings.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-[#B8A891] py-8">No day outings yet.</TableCell></TableRow>
                  )}
                  {outings.map((o) => (
                    <TableRow key={o.id} data-testid={`outing-row-${o.id}`} className="border-white/10">
                      <TableCell className="text-[#F2E7D0]">{o.full_name}<br/><span className="text-[10px] text-[#B8A891] font-mono-label">#{(o.id || "").substring(0,8).toUpperCase()}</span></TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{o.email}<br/>{o.phone}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{o.date}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">{o.arrival_time}</TableCell>
                      <TableCell className="text-[#B8A891]">{o.guests}</TableCell>
                      <TableCell className="text-[#B8A891] text-xs">
                        {o.payment_status === "paid"
                          ? <span className="text-[#F2E7D0]">₹ {(o.amount_paid_inr || o.total_inr || 0).toLocaleString("en-IN")}</span>
                          : <span className="text-amber-400">{o.payment_status}</span>}
                      </TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={(v) => updateOuting(o.id, v)}>
                          <SelectTrigger className="h-9 rounded-none bg-transparent border-white/20 w-36 font-mono-label text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                            {OUTING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button data-testid={`delete-outing-${o.id}`} onClick={() => deleteOuting(o.id)} variant="ghost" size="icon"><Trash2 size={16} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="discounts" className="mt-6">
            <AdminDiscounts token={token} />
          </TabsContent>
        </Tabs>
      </section>
    </main>
  );
}
